import { Effect, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import { defineMessageUnion } from "foldkit/message";
import { defineView } from "foldkit/submodel";
import * as Update from "foldkit/update";
import { apiData, settle } from "../api/asyncData";
import { createNoteEntry } from "../api/commands/noteEntry";
import { getRecords } from "../api/commands/records";
import { ApiError, toMessage } from "../api/errors";
import { ENTRY_TYPES, Page } from "../api/schemas/common";
import { Entry } from "../api/schemas/entries";
import { RecordSummary } from "../api/schemas/records";
import { namedInteractions } from "../app/namedInteractions";
import { Route, toHref } from "../app/routes";
import { entrySubtypeLabel, i18next } from "../i18n";
import type { Api, Shared } from "../pages/shared";
import { parseTags } from "./addEntry";
import { busyAttributes, busySpinner } from "./busy";
import { Dialog } from "./dialog";

const Person = S.Struct({ id: S.String, name: S.String });
export const Model = S.Struct({
	dialog: Dialog.Model,
	person: S.Option(Person),
	query: S.String,
	people: apiData(Page(RecordSummary)).schema,
	body: S.String,
	title: S.String,
	tags: S.String,
	type: S.String,
	saving: S.Boolean,
	error: S.Option(S.String),
	saved: S.Option(Person),
});
export type Model = typeof Model.Type;
export const init = (): Model => ({
	dialog: Dialog.init({ id: "log-interaction" }),
	person: Option.none(),
	query: "",
	people: AsyncData.Idle(),
	body: "",
	title: "",
	tags: "",
	type: "note",
	saving: false,
	error: Option.none(),
	saved: Option.none(),
});
export const Message = defineMessageUnion({
	Opened: { person: S.Option(Person) },
	GotDialogMessage: { message: Dialog.Message },
	ChangedQuery: { value: S.String },
	Searched: {},
	CompletedSearch: {
		query: S.String,
		generation: S.Number,
		result: getRecords.Result,
	},
	SelectedPerson: { id: S.String },
	ChangedPerson: {},
	ChangedBody: { value: S.String },
	ChangedTitle: { value: S.String },
	ChangedTags: { value: S.String },
	ChangedType: { value: S.String },
	Submitted: {},
	CompletedSave: { generation: S.Number, result: S.Result(Entry, ApiError) },
	DismissedSuccess: {},
});
export type Message = typeof Message.Type;
export const OutMessage = defineMessageUnion({ Saved: { nodeId: S.String } });
export type OutMessage = typeof OutMessage.Type;
type Return = Update.ReturnWithOutMessage<Model, Message, OutMessage, Api>;
const t = (key: string) => i18next.t(`logInteraction.${key}`);
export const noteTypes = ENTRY_TYPES.filter(
	(type) => type !== "tasks" && type !== "upload",
);
export const SearchPeople = getRecords.command(
	Message.CompletedSearch,
	({ search, generation, result }) =>
		Message.CompletedSearch({ query: search, generation, result }),
);
export const Save = Command.define("LogInteraction", {
	args: {
		nodeId: S.String,
		body: S.String,
		title: S.String,
		tags: S.Array(S.String),
		type: S.String,
		generation: S.Number,
	},
	messages: [Message.CompletedSave],
	execute: ({ nodeId, generation, ...note }) =>
		createNoteEntry(nodeId, note).pipe(
			Effect.map((result) => Message.CompletedSave({ generation, result })),
		),
});
const dialogSlot = {
	read: (model: Model) => Option.some(model.dialog),
	write: (model: Model, dialog: Dialog.Model): Model => ({ ...model, dialog }),
	toParentMessage: (message: Dialog.Message) =>
		Message.GotDialogMessage({ message }),
	foldOutMessage:
		() =>
		(model: Model): Return => ({ model }),
};
const open = Update.foldChildStep({ ...dialogSlot, update: Dialog.open });
const close = Update.foldChildStep({ ...dialogSlot, update: Dialog.close });
const foldDialog = Update.foldChild({ ...dialogSlot, update: Dialog.update });
const search = (model: Model, shared: Shared): Return => ({
	model: { ...model, people: AsyncData.Loading() },
	commands: [
		SearchPeople({
			search: model.query.trim(),
			cursor: 0,
			asOf: null,
			generation: shared.generation,
		}),
	],
});
const hasDraft = (model: Model) =>
	model.body.length > 0 ||
	model.title.length > 0 ||
	model.tags.length > 0 ||
	model.type !== "note";
export const canSave = (model: Model) =>
	!model.saving && Option.isSome(model.person) && model.body.trim().length > 0;
export const update = (
	model: Model,
	message: Message,
	shared: Shared,
): Return => {
	if (
		Option.isSome(shared.asOf) &&
		message._tag !== "GotDialogMessage" &&
		message._tag !== "CompletedSave"
	)
		return { model };
	// Once submitted, the target and note remain fixed until the request settles.
	if (
		model.saving &&
		message._tag !== "CompletedSave" &&
		message._tag !== "GotDialogMessage"
	)
		return { model };
	return Message.match<Return>(message, {
		Opened: ({ person }) => {
			const opened = open({
				...model,
				person: hasDraft(model) ? model.person : person,
				saved: Option.none(),
			});
			const result: Return = Option.isSome(opened.model.person)
				? { model: opened.model }
				: search(opened.model, shared);
			return {
				...result,
				commands: [...(opened.commands ?? []), ...(result.commands ?? [])],
			};
		},
		GotDialogMessage: ({ message }) => foldDialog(model, message),
		ChangedQuery: ({ value }) => ({
			model: { ...model, query: value, people: AsyncData.Idle() },
		}),
		Searched: () => search(model, shared),
		CompletedSearch: ({ query, generation, result }) =>
			generation === shared.generation && query === model.query.trim()
				? { model: { ...model, people: settle(model.people, result) } }
				: { model },
		SelectedPerson: ({ id }) => {
			const person = AsyncData.getOrElse(model.people, () => ({
				data: [],
			})).data.find((p) => p.id === id);
			return {
				model: person
					? {
							...model,
							person: Option.some({ id: person.id, name: person.name }),
						}
					: model,
			};
		},
		ChangedPerson: () =>
			search({ ...model, person: Option.none(), query: "" }, shared),
		ChangedBody: ({ value }) => ({ model: { ...model, body: value } }),
		ChangedTitle: ({ value }) => ({ model: { ...model, title: value } }),
		ChangedTags: ({ value }) => ({ model: { ...model, tags: value } }),
		ChangedType: ({ value }) => ({
			model: {
				...model,
				type: noteTypes.some((type) => type === value) ? value : "note",
			},
		}),
		Submitted: () =>
			!canSave(model) || Option.isNone(model.person)
				? { model }
				: {
						model: { ...model, saving: true, error: Option.none() },
						commands: [
							Save({
								nodeId: model.person.value.id,
								body: model.body.trim(),
								title: model.title.trim(),
								tags: parseTags(model.tags),
								type: model.type,
								generation: shared.generation,
							}),
						],
					},
		CompletedSave: ({ generation, result }) => {
			if (
				generation !== shared.generation ||
				!model.saving ||
				Option.isNone(model.person)
			)
				return { model };
			const person = model.person;
			return Result.match(result, {
				onFailure: (error): Return => ({
					model: {
						...model,
						saving: false,
						error: Option.some(toMessage(error)),
					},
				}),
				onSuccess: (): Return => {
					const closed = close(model);
					return {
						...closed,
						model: { ...init(), dialog: closed.model.dialog, saved: person },
						outMessage: OutMessage.Saved({ nodeId: person.value.id }),
					};
				},
			});
		},
		DismissedSuccess: () => ({ model: { ...model, saved: Option.none() } }),
	});
};

export const view = defineView<Model, Message, Shared>((model, _shared, h) => {
	const button = (
		label: string,
		message: Message,
		className = "btn btn-ghost btn-sm",
	) =>
		h.button(
			[
				h.Type("button"),
				h.Class(className),
				h.AriaLabel(label),
				h.Disabled(model.saving),
				h.OnClick(message),
			],
			[label],
		);
	const people = AsyncData.getOrElse(model.people, () => ({ data: [] })).data;
	const searching =
		AsyncData.isLoading(model.people) || AsyncData.isRefreshing(model.people);
	return h.div(
		[],
		[
			Option.match(model.saved, {
				onNone: () => h.empty,
				onSome: (person) =>
					h.div(
						[h.Class("toast toast-bottom toast-center z-50 max-w-full")],
						[
							h.div(
								[
									h.Role("status"),
									h.Class("alert alert-success flex flex-wrap"),
								],
								[
									i18next.t("logInteraction.saved", { name: person.name }),
									h.a(
										[
											h.Class("link"),
											h.Href(
												toHref(
													Route.Record({ id: person.id, asOf: Option.none() }),
												),
											),
										],
										[t("viewPerson")],
									),
									button(t("dismiss"), Message.DismissedSuccess()),
								],
							),
						],
					),
			}),
			h.submodel({
				slotId: "interaction-dialog",
				model: model.dialog,
				view: Dialog.view,
				toParentMessage: (message: Dialog.Message) =>
					Message.GotDialogMessage({ message }),
				viewInputs: {
					toView: (render) =>
						h.dialog(
							[...render.dialog, h.Class("modal")],
							render.isVisible
								? [
										h.div([...render.backdrop, h.Class("modal-backdrop")], []),
										h.div(
											[
												...render.panel,
												h.Class(
													"modal-box w-[calc(100%-2rem)] max-w-lg max-h-[85dvh] overflow-y-auto",
												),
											],
											[
												h.div(
													[
														h.Class(
															"flex items-center justify-between gap-2 mb-4",
														),
													],
													[
														h.h2(
															[
																...render.title,
																h.Class("text-xl font-semibold"),
															],
															[t("title")],
														),
														h.button(
															[
																...render.closeButton,
																h.Type("button"),
																h.Class("btn btn-sm btn-circle btn-ghost"),
																h.AriaLabel(t("close")),
															],
															["✕"],
														),
													],
												),
												h.p(
													[
														...render.description,
														h.Class("text-sm text-base-content/70 mb-4"),
													],
													[t("description")],
												),
												h.div(
													[h.Class("space-y-3 mb-4")],
													[
														h.p([h.Class("font-medium")], [t("person")]),
														Option.match(model.person, {
															onSome: (person) =>
																h.div(
																	[
																		h.Class(
																			"flex items-center justify-between gap-2 rounded-lg bg-base-200 p-3",
																		),
																	],
																	[
																		h.span(
																			[h.Class("break-words min-w-0")],
																			[person.name],
																		),
																		button(
																			t("change"),
																			Message.ChangedPerson(),
																		),
																	],
																),
															onNone: () =>
																h.div(
																	[h.Class("space-y-2")],
																	[
																		h.form(
																			[
																				h.OnSubmit(Message.Searched()),
																				h.Class("flex gap-2"),
																			],
																			[
																				h.input([
																					...render.initialFocus,
																					h.Type("search"),
																					h.Class(
																						"input input-bordered w-full min-w-0",
																					),
																					h.AriaLabel(t("search")),
																					h.Placeholder(t("search")),
																					h.Value(model.query),
																					h.OnInput((value) =>
																						Message.ChangedQuery({ value }),
																					),
																				]),
																				h.button(
																					[
																						h.Type("submit"),
																						h.Class("btn"),
																						h.AriaLabel(t("searchButton")),
																						...busyAttributes(h, searching),
																					],
																					[
																						busySpinner(h, searching),
																						t("searchButton"),
																					],
																				),
																			],
																		),
																		AsyncData.isLoading(model.people)
																			? h.p([h.Role("status")], [t("loading")])
																			: AsyncData.isFailure(model.people)
																				? h.div(
																						[h.Role("alert")],
																						[
																							t("searchError"),
																							button(
																								t("retry"),
																								Message.Searched(),
																							),
																						],
																					)
																				: AsyncData.isIdle(model.people)
																					? h.p(
																							[
																								h.Class(
																									"text-sm text-base-content/70",
																								),
																							],
																							[t("searchHint")],
																						)
																					: people.length === 0
																						? h.p(
																								[h.Class("text-sm")],
																								[t("noPeople")],
																							)
																						: h.div(
																								[
																									h.Class(
																										"max-h-40 overflow-y-auto space-y-1",
																									),
																								],
																								people.map((person) =>
																									h.button(
																										[
																											h.Type("button"),
																											h.Class(
																												"btn btn-ghost h-auto min-h-10 w-full justify-start text-left py-2",
																											),
																											h.AriaLabel(
																												i18next.t(
																													"logInteraction.select",
																													{ name: person.name },
																												),
																											),
																											h.OnClick(
																												Message.SelectedPerson({
																													id: person.id,
																												}),
																											),
																										],
																										[
																											h.span(
																												[
																													h.Class(
																														"break-words",
																													),
																												],
																												[
																													person.name,
																													person.subtitle
																														? ` — ${person.subtitle}`
																														: "",
																												],
																											),
																										],
																									),
																								),
																							),
																	],
																),
														}),
													],
												),
												h.form(
													[
														h.OnSubmit(Message.Submitted()),
														h.Class("space-y-4"),
													],
													[
														h.label(
															[h.Class("block space-y-2")],
															[
																h.span([h.Class("font-medium")], [t("body")]),
																h.textarea([
																	...(Option.isSome(model.person)
																		? render.initialFocus
																		: []),
																	h.Class(
																		"textarea textarea-bordered w-full min-h-32",
																	),
																	h.AriaLabel(t("body")),
																	h.Placeholder(t("placeholder")),
																	h.Value(model.body),
																	h.Disabled(model.saving),
																	h.OnInput((value) =>
																		Message.ChangedBody({ value }),
																	),
																]),
															],
														),
														h.details(
															[
																h.Class(
																	"rounded-lg border border-base-300 p-3",
																),
															],
															[
																h.summary(
																	[
																		h.DataAttribute(
																			"analytics-event",
																			namedInteractions.interactionDetails,
																		),
																		h.Class(
																			"cursor-pointer text-sm font-medium",
																		),
																	],
																	[t("more")],
																),
																h.div(
																	[h.Class("space-y-3 mt-3")],
																	[
																		h.label(
																			[h.Class("block")],
																			[
																				t("optionalTitle"),
																				h.input([
																					h.Type("text"),
																					h.Class(
																						"input input-bordered w-full",
																					),
																					h.AriaLabel(t("optionalTitle")),
																					h.Value(model.title),
																					h.Disabled(model.saving),
																					h.OnInput((value) =>
																						Message.ChangedTitle({ value }),
																					),
																				]),
																			],
																		),
																		h.label(
																			[h.Class("block")],
																			[
																				t("tags"),
																				h.input([
																					h.Type("text"),
																					h.Class(
																						"input input-bordered w-full",
																					),
																					h.AriaLabel(t("tags")),
																					h.Placeholder(t("tagsHint")),
																					h.Value(model.tags),
																					h.Disabled(model.saving),
																					h.OnInput((value) =>
																						Message.ChangedTags({ value }),
																					),
																				]),
																			],
																		),
																		h.label(
																			[h.Class("block")],
																			[
																				t("type"),
																				h.select(
																					[
																						h.Class(
																							"select select-bordered w-full",
																						),
																						h.AriaLabel(t("type")),
																						h.Value(model.type),
																						h.Disabled(model.saving),
																						h.OnChange((value) =>
																							Message.ChangedType({ value }),
																						),
																					],
																					noteTypes.map((type) =>
																						h.option(
																							[
																								h.Value(type),
																								h.Selected(model.type === type),
																							],
																							[entrySubtypeLabel(type)],
																						),
																					),
																				),
																			],
																		),
																	],
																),
															],
														),
														Option.match(model.error, {
															onNone: () => h.empty,
															onSome: (error) =>
																h.p(
																	[
																		h.Role("alert"),
																		h.Class("text-error text-sm"),
																	],
																	[t("saveError"), " ", error],
																),
														}),
														h.div(
															[
																h.Class(
																	"flex items-center justify-between gap-3",
																),
															],
															[
																h.span(
																	[h.Class("text-xs text-base-content/60")],
																	[t("draftHint")],
																),
																h.button(
																	[
																		h.Type("submit"),
																		h.Class("btn btn-primary"),
																		h.AriaLabel(t("save")),
																		...busyAttributes(
																			h,
																			model.saving,
																			!canSave(model),
																		),
																	],
																	[
																		busySpinner(h, model.saving),
																		model.saving ? t("saving") : t("save"),
																	],
																),
															],
														),
													],
												),
											],
										),
									]
								: [],
						),
				},
			}),
		],
	);
});
